import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtNum, fmtDateTime } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { printDoc, esc } from "@/lib/print";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Banknote, Percent, ShoppingCart, TrendingUp, Boxes, CalendarClock, XCircle, Bike, Download, Printer, Package, PackageX,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tone = "slate", sub }) {
  const tones = {
    slate: "bg-muted text-muted-foreground",
    sky: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
    emerald: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
    rose: "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
    violet: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  };
  return (
    <Card className="p-4 shadow-sm border-border" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-2xl font-bold text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

const ctypeLabel = (t) => ({ cash: "Cash / retail", corporate: "Corporate", insurance: "Insurance" }[t] || t);

export default function Analytics() {
  const { branchParam, activeBranch, branches } = useBranch();
  const isoDay = (d) => d.toISOString().slice(0, 10);
  const [to, setTo] = useState(isoDay(new Date()));
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 29 * 86400000)));
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/analytics", { params: { branch_id: branchParam, date_from: from, date_to: to } }).then((r) => setData(r.data)).catch(() => {});
    api.get("/reports/sales-lineitems", { params: { branch_id: branchParam, date_from: from, date_to: to } }).then((r) => setItems(r.data)).catch(() => {});
  }, [branchParam, from, to]);
  useEffect(() => { load(); }, [load]);

  const exportSales = () => {
    if (!items.length) return;
    downloadCSV("sales-line-items.csv", items, [
      { key: "invoice_no", label: "Invoice" }, { key: "date", label: "Date" }, { key: "branch", label: "Branch" },
      { key: "customer", label: "Customer" }, { key: "customer_type", label: "Customer type" },
      { key: "payment_method", label: "Payment" }, { key: "fulfillment", label: "Fulfillment" },
      { key: "category", label: "Category" }, { key: "sku", label: "SKU" }, { key: "generic_name", label: "Medicine" },
      { key: "quantity", label: "Qty" }, { key: "unit_price", label: "Unit price" }, { key: "line_total", label: "Line total" },
      { key: "cost", label: "Cost" }, { key: "profit", label: "Profit" },
    ]);
  };

  const fetchInventory = async () => {
    const { data: b } = await api.get("/batches", { params: { branch_id: branchParam } });
    return b.map((x) => ({
      branch: x.branch_name, sku: x.sku, medicine: x.generic_name, brand: x.brand,
      batch_no: x.batch_no, expiry_date: x.expiry_date, quantity_on_hand: x.quantity_on_hand,
      cost_price: x.cost_price, stock_value: Number(((x.quantity_on_hand || 0) * (x.cost_price || 0)).toFixed(2)),
    }));
  };
  const exportInventory = async () => {
    const rows = await fetchInventory();
    downloadCSV("inventory-batches.csv", rows, [
      { key: "branch", label: "Branch" }, { key: "sku", label: "SKU" }, { key: "medicine", label: "Medicine" },
      { key: "brand", label: "Brand" }, { key: "batch_no", label: "Batch" }, { key: "expiry_date", label: "Expiry" },
      { key: "quantity_on_hand", label: "Qty on hand" }, { key: "cost_price", label: "Unit cost" }, { key: "stock_value", label: "Stock value" },
    ]);
  };
  const printInventory = async () => {
    const rows = await fetchInventory();
    const total = rows.reduce((s, r) => s + r.stock_value, 0);
    const body = `<h1>MobiMed — Inventory Valuation</h1><div class="muted">${esc(scope)} · ${new Date().toLocaleDateString()}</div>
      <table><thead><tr><th>Branch</th><th>SKU</th><th>Medicine</th><th>Batch</th><th>Expiry</th><th class="r">Qty</th><th class="r">Unit cost</th><th class="r">Value</th></tr></thead>
      <tbody>${rows.map((r) => `<tr><td>${esc(r.branch)}</td><td>${esc(r.sku)}</td><td>${esc(r.medicine)}</td><td>${esc(r.batch_no)}</td><td>${esc(r.expiry_date)}</td><td class="r">${r.quantity_on_hand}</td><td class="r">${fmtGHS(r.cost_price)}</td><td class="r">${fmtGHS(r.stock_value)}</td></tr>`).join("")}</tbody>
      <tfoot><tr><td colspan="7" class="r">Total stock value</td><td class="r">${fmtGHS(total)}</td></tr></tfoot></table>`;
    printDoc("Inventory Valuation", body);
  };

  const scope = activeBranch === "all" ? "Network-wide" : (branches.find((b) => b.id === activeBranch)?.name || "");
  if (!data) return <div className="text-muted-foreground">Loading analytics…</div>;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="HQ Analytics" subtitle={`${scope} · ${from} → ${to}`}
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" data-testid="analytics-from" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9" />
            <span className="text-muted-foreground text-sm">→</span>
            <Input type="date" data-testid="analytics-to" value={to} min={from} max={isoDay(new Date())} onChange={(e) => setTo(e.target.value)} className="w-40 h-9" />
            <Button variant="outline" data-testid="export-sales-csv-btn" onClick={exportSales}><Download className="h-4 w-4 mr-1.5" /> Sales CSV</Button>
          </div>
        } />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Kpi icon={Banknote} label="Sales" value={fmtGHS(k.total_sales)} tone="emerald" sub={`Today ${fmtGHS(k.today_sales)}`} />
        <Kpi icon={Percent} label="Gross profit" value={fmtGHS(k.gross_profit)} tone="sky" sub={`Margin ${k.margin_pct}%`} />
        <Kpi icon={ShoppingCart} label="Orders" value={fmtNum(k.order_count)} tone="slate" sub={`Avg ${fmtGHS(k.avg_order_value)}`} />
        <Kpi icon={Bike} label="Deliveries today" value={fmtNum(k.deliveries_today)} tone="violet" sub={`Sales ${fmtGHS(k.delivery_sales)} · profit ${fmtGHS(k.delivery_profit)}`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Boxes} label="Inventory value" value={fmtGHS(k.inventory_cost_value)} tone="sky" sub={`Retail ${fmtGHS(k.inventory_retail_value)}`} />
        <Kpi icon={CalendarClock} label="Expiring ≤30d" value={fmtGHS(k.expiry_30_value)} tone="amber" sub={`60d ${fmtGHS(k.expiry_60_value)} · 90d ${fmtGHS(k.expiry_90_value)}`} />
        <Kpi icon={XCircle} label="Already expired" value={fmtGHS(k.expired_value)} tone="rose" sub="At cost, on shelf" />
        <div className="md:col-span-1 flex items-end gap-2">
          <Button variant="outline" className="flex-1" data-testid="export-inventory-csv-btn" onClick={exportInventory}><Download className="h-4 w-4 mr-1.5" /> Inventory CSV</Button>
          <Button variant="outline" data-testid="print-inventory-btn" onClick={printInventory}><Printer className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card className="p-5 shadow-sm mb-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-muted-foreground" /> Sales trend</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.sales_trend}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip formatter={(v) => fmtGHS(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Line type="monotone" dataKey="sales" stroke="#0284c7" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 shadow-sm">
          <h3 className="font-semibold text-foreground mb-4">Sales & profit by branch <span className="text-xs text-muted-foreground font-normal">(deliveries consolidated)</span></h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.sales_by_branch}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="branch" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtGHS(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" name="Sales" fill="#0284c7" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 shadow-sm">
          <h3 className="font-semibold text-foreground mb-4">Sales by customer type</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.sales_by_customer_type.map((c) => ({ ...c, label: ctypeLabel(c.type) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtGHS(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="sales" name="Sales" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 shadow-sm overflow-x-auto">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><Package className="h-4 w-4 text-muted-foreground" /> Top-selling medicines</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Medicine</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Sales</th></tr></thead>
            <tbody>
              {data.top_medicines.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">No sales in period</td></tr>}
              {data.top_medicines.map((m) => (
                <tr key={m.sku} className="border-b border-border" data-testid={`top-med-${m.sku}`}>
                  <td className="py-2">{m.generic_name}<span className="text-xs text-muted-foreground ml-1">{m.sku}</span></td>
                  <td className="py-2 text-right font-mono">{fmtNum(m.quantity)}</td>
                  <td className="py-2 text-right font-mono">{fmtGHS(m.sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-5 shadow-sm overflow-x-auto">
          <h3 className="font-semibold text-foreground mb-3">Margin by category</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Category</th><th className="py-2 text-right">Sales</th><th className="py-2 text-right">Profit</th><th className="py-2 text-right">Margin</th></tr></thead>
            <tbody>
              {data.margin_by_category.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No sales in period</td></tr>}
              {data.margin_by_category.map((c) => (
                <tr key={c.category} className="border-b border-border">
                  <td className="py-2">{c.category}</td>
                  <td className="py-2 text-right font-mono">{fmtGHS(c.sales)}</td>
                  <td className="py-2 text-right font-mono text-emerald-600">{fmtGHS(c.profit)}</td>
                  <td className="py-2 text-right font-mono text-muted-foreground">{c.margin_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <Card className="p-5 shadow-sm mb-6 overflow-x-auto" data-testid="top-unmet-demand-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2"><PackageX className="h-4 w-4 text-rose-500" /> Top unmet demand</h3>
          <div className="text-xs text-muted-foreground">Est. lost revenue <span className="font-semibold text-rose-600" data-testid="analytics-lost-revenue">{fmtGHS(k.missed_lost_revenue || 0)}</span></div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Medicine</th><th className="py-2">Type</th><th className="py-2 text-right">Requests</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Est. lost revenue</th></tr></thead>
          <tbody>
            {(data.top_unmet_demand || []).length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No out-of-stock demand logged in period</td></tr>}
            {(data.top_unmet_demand || []).map((m, i) => (
              <tr key={i} className="border-b border-border" data-testid={`unmet-row-${i}`}>
                <td className="py-2">{m.product_name}{m.sku && <span className="text-xs text-muted-foreground ml-1 font-mono">{m.sku}</span>}</td>
                <td className="py-2">{m.is_catalog
                  ? <span className="text-xs rounded-full px-2 py-0.5 bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">Out of stock</span>
                  : <span className="text-xs rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Not carried</span>}</td>
                <td className="py-2 text-right font-mono">{fmtNum(m.requests)}</td>
                <td className="py-2 text-right font-mono font-semibold">{fmtNum(m.quantity)}</td>
                <td className="py-2 text-right font-mono">{m.is_catalog ? fmtGHS(m.est_lost_revenue) : <span className="text-muted-foreground">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-5 shadow-sm mb-6 overflow-x-auto">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-muted-foreground" /> Slow movers & stock cover</h3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Medicine</th><th className="py-2 text-right">On hand</th><th className="py-2 text-right">Sold ({data.period_days}d)</th><th className="py-2 text-right">Cover (days)</th></tr></thead>
          <tbody>
            {data.slow_movers.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Nothing flagged — healthy turnover</td></tr>}
            {data.slow_movers.map((s) => (
              <tr key={s.sku} className="border-b border-border">
                <td className="py-2">{s.generic_name}<span className="text-xs text-muted-foreground ml-1">{s.sku}</span></td>
                <td className="py-2 text-right font-mono">{fmtNum(s.on_hand)}</td>
                <td className="py-2 text-right font-mono">{fmtNum(s.sold_period)}</td>
                <td className="py-2 text-right font-mono">{s.cover_days == null ? <span className="text-rose-600">No sales</span> : s.cover_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-5 shadow-sm overflow-x-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground">Sales data repository <span className="text-xs text-muted-foreground font-normal">({fmtNum(items.length)} line items)</span></h3>
          <Button size="sm" variant="outline" data-testid="repo-export-btn" onClick={exportSales}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Invoice</th><th className="py-2">When</th><th className="py-2">Branch</th><th className="py-2">Medicine</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Total</th><th className="py-2 text-right">Profit</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No sales line items</td></tr>}
            {items.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-2 font-mono text-xs text-sky-700">{r.invoice_no}</td>
                <td className="py-2 text-xs text-muted-foreground">{fmtDateTime(r.date)}</td>
                <td className="py-2 text-muted-foreground">{r.branch}</td>
                <td className="py-2">{r.generic_name}<span className="text-xs text-muted-foreground ml-1">{r.sku}</span></td>
                <td className="py-2 text-right font-mono">{r.quantity}</td>
                <td className="py-2 text-right font-mono">{fmtGHS(r.line_total)}</td>
                <td className="py-2 text-right font-mono text-emerald-600">{fmtGHS(r.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > 100 && <div className="text-xs text-muted-foreground mt-2">Showing first 100 rows — export CSV for the full {fmtNum(items.length)} line items.</div>}
      </Card>
    </div>
  );
}
